import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/ui/StatusBadge';
import { Panel, PanelHeader } from '@/components/ui/Panel';
import { ApiError } from '@/lib/api';
import { ordersApi, poTone, type OrderRow } from '@/lib/orders';
import { aed, shortDate } from '@/lib/format';

/** Карточка заказа поставщику на данных Zoho Books: позиции, суммы, статусы, ссылка в Zoho, поставщик. */
export default function PurchaseOrderDetailPage() {
  const { t, i18n } = useTranslation();
  const { poNumber } = useParams<{ poNumber: string }>();
  const [po, setPo] = useState<OrderRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poNumber) return;
    ordersApi.get(poNumber).then(setPo).catch((e: ApiError) => setError(e.code));
  }, [poNumber]);

  if (error) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        <p className="text-sm font-medium text-foreground-900">{t('poDetail.notFound')}</p>
        <Link to="/purchase-orders" className="mt-2 inline-block text-[12px] font-semibold text-primary-700">{t('poDetail.backToPo')}</Link>
      </div>
    );
  }
  if (!po) return <div className="p-8 text-center text-[12.5px] text-foreground-500">…</div>;

  const items = po.items ?? [];
  const subtotal = items.reduce((s, i) => s + i.total, 0);
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
  const waText = encodeURIComponent(t('poDetail.waMessage', { partner: po.partnerName, po: po.poNumber, lines: items.length, units: po.units, total: po.total.toLocaleString('en-AE'), date: po.expectedAt ? shortDate(po.expectedAt) : '—' }));
  const timeline = [
    { label: t('poDetail.timeline1'), at: po.orderedAt ? shortDate(po.orderedAt) : null, done: true },
    { label: t('poDetail.timeline2'), at: po.sentAt ? fmt(po.sentAt) : null, done: po.fulfillmentStatus !== 'Draft' },
    { label: t('ord.timelineReceived'), at: po.pickedUpAt ? fmt(po.pickedUpAt) : null, done: ['PickedUp', 'PartiallyPickedUp', 'Closed'].includes(po.fulfillmentStatus) },
    { label: t('ord.timelineBilled'), at: po.billedStatus === 'billed' ? t('ord.billed') : null, done: po.billedStatus === 'billed' || po.fulfillmentStatus === 'Closed' },
  ];

  return (
    <div>
      <Link to="/purchase-orders" className="mb-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line text-[12px]" /> {t('poDetail.back')}
      </Link>
      <div className="flex flex-col gap-4 border-b border-background-200 pb-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="tnum text-lg font-semibold text-foreground-950 md:text-xl">{po.poNumber}</h1>
            <StatusBadge tone={poTone(po.fulfillmentStatus) as 'primary' | 'secondary' | 'accent' | 'neutral' | 'danger'}>{t(`status.${po.fulfillmentStatus}`, po.fulfillmentStatus)}</StatusBadge>
            {po.zohoStatus && <StatusBadge tone="neutral" dot={false}>Zoho: {po.zohoStatus}</StatusBadge>}
          </div>
          <p className="mt-1 text-[12px] text-foreground-500">
            {po.partnerName} · {t('ord.ordered')} {po.orderedAt ? shortDate(po.orderedAt) : '—'} · {t('ord.expected')} {po.expectedAt ? shortDate(po.expectedAt) : '—'}
            {po.reference && <> · {t('ord.colReference')}: {po.reference}</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {po.zohoUrl && (
            <a href={po.zohoUrl} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100">
              <i className="ri-external-link-line text-[14px]" /> {t('ord.openZoho')}
            </a>
          )}
          {po.partnerPhone && (
            <a href={`https://wa.me/${po.partnerPhone.replace(/[^0-9]/g, '')}?text=${waText}`} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600">
              <i className="ri-whatsapp-line text-[14px]" /> {t('poDetail.sendWa')}
            </a>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <Panel padded={false}>
            <PanelHeader title={t('poDetail.itemsTitle')} subtitle={t('poDetail.itemsMeta', { lines: items.length, units: po.units })} />
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-[12px]">
                <thead>
                  <tr className="border-y border-background-200 bg-background-100/70 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                    <th className="px-4 py-2 font-medium">{t('poDetail.colSku')}</th>
                    <th className="px-3 py-2 font-medium">{t('poDetail.colDescription')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('poDetail.colQty')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('ord.colReceived')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('poDetail.colUnitCost')}</th>
                    <th className="px-4 py-2 text-right font-medium">{t('poDetail.colAmount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-background-200/70">
                      <td className="tnum px-4 py-2 align-top text-foreground-600">{it.sku ? <Link to={`/catalog/${encodeURIComponent(it.sku)}`} className="text-primary-700">{it.sku}</Link> : it.supplierArticle || '—'}</td>
                      <td className="px-3 py-2 align-top text-foreground-900">{it.name}</td>
                      <td className="tnum px-3 py-2 text-right align-top">{it.qty}</td>
                      <td className={`tnum px-3 py-2 text-right align-top ${it.qtyReceived >= it.qty ? 'text-primary-700' : 'text-foreground-500'}`}>{it.qtyReceived}</td>
                      <td className="tnum px-3 py-2 text-right align-top text-foreground-600">{it.unitCost.toFixed(2)}</td>
                      <td className="tnum px-4 py-2 text-right align-top font-medium text-foreground-900">{it.total.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col items-end gap-1 px-4 py-3">
              <div className="flex w-full max-w-[260px] justify-between text-[11.5px]"><span className="text-foreground-500">{t('poDetail.subtotal')}</span><span className="tnum text-foreground-900">{aed(subtotal, 2)}</span></div>
              <div className="flex w-full max-w-[260px] justify-between text-[11.5px]"><span className="text-foreground-500">{t('ord.vat')}</span><span className="tnum text-foreground-900">{aed(po.vatTotal, 2)}</span></div>
              <div className="mt-1 flex w-full max-w-[260px] justify-between border-t border-background-300 pt-1.5 text-[12.5px] font-semibold"><span className="text-foreground-950">{t('poDetail.totalInclVat')}</span><span className="tnum text-foreground-950">{aed(po.total, 2)} {po.currency !== 'AED' ? po.currency : ''}</span></div>
            </div>
          </Panel>
        </div>
        <div className="flex flex-col gap-4">
          <Panel>
            <h2 className="text-sm font-semibold text-foreground-950">{t('poDetail.statusTitle')}</h2>
            <ol className="mt-3 space-y-3">
              {timeline.map((s) => (
                <li key={s.label} className="flex items-start gap-3">
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] ${s.done ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-500'}`}>{s.done ? <i className="ri-check-line" /> : ''}</span>
                  <span className="min-w-0"><span className="block text-[12.5px] font-medium text-foreground-900">{s.label}</span><span className="block text-[11px] text-foreground-500">{s.at ?? '—'}</span></span>
                </li>
              ))}
            </ol>
            <p className="mt-3 border-t border-background-200 pt-3 text-[11px] text-foreground-400">{t('ord.syncedAt', { date: fmt(po.lastSyncedAt) })}</p>
          </Panel>
          <Panel>
            <h2 className="text-sm font-semibold text-foreground-950">{t('poDetail.partnerTitle')}</h2>
            <p className="mt-2 text-[13px] font-medium text-foreground-900">{po.partnerName}</p>
            {po.vendorName && po.vendorName !== po.partnerName && <p className="text-[11px] text-foreground-500">Zoho: {po.vendorName}</p>}
            {po.pickupAddress && <p className="mt-1 text-[12px] text-foreground-600">{po.pickupAddress}</p>}
            {po.partnerPhone && <p className="tnum mt-1 text-[12px] text-foreground-600">{po.partnerPhone}</p>}
            <dl className="mt-3 space-y-1.5 border-t border-background-200 pt-3 text-[12px]">
              <div className="flex justify-between"><dt className="text-foreground-500">{t('ord.billedStatus')}</dt><dd className="font-medium text-foreground-900">{po.billedStatus ?? '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-foreground-500">{t('ord.receivedStatus')}</dt><dd className="font-medium text-foreground-900">{po.receivedStatus ?? '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-foreground-500">{t('ord.paymentStatus')}</dt><dd className="font-medium text-foreground-900">{po.paymentStatus}</dd></div>
            </dl>
            <Link to={`/partners/${po.partnerId}`} className="mt-3 inline-block text-[12px] font-semibold text-primary-700">{t('poDetail.openPartner')}</Link>
          </Panel>
        </div>
      </div>
    </div>
  );
}
